import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Palette, ChevronDown, ChevronRight } from "lucide-react";
import { SYMBOLS, LINE_STYLES, HATCHES, WIDTHS, ColorPicker, LookSwatch, kindLabel, setLegendCache } from "@/components/takeoff/takeoffShared";

const SEL = "text-xs border border-slate-200 rounded px-1.5 py-1 bg-white disabled:opacity-40";

function LegendRow({ kind, e, canEdit, onChange }) {
  const mt = e.measure_type || (e.hatch && e.hatch !== "none" ? "area" : "count");
  return (
    <tr className="border-t border-slate-100" data-testid={`legend-row-${kind}`}>
      <td className="py-1.5 pr-2"><LookSwatch look={e} type={mt} testid={`legend-swatch-${kind}`} /></td>
      <td className="py-1.5 pr-2 text-xs text-slate-800">{kindLabel(kind)}</td>
      <td className="py-1.5 pr-2">{canEdit ? <span className="inline-flex items-center gap-1"><ColorPicker value={e.color} onChange={(c) => onChange({ color: c })} testid={`legend-color-${kind}`} size="sm" /><span className="text-[10px] font-mono text-slate-500">{e.color}</span></span> : <span className="text-[10px] font-mono">{e.color}</span>}</td>
      <td className="py-1.5 pr-2"><select disabled={!canEdit || mt !== "count" && e.measure_type != null} value={e.symbol || "circle"} onChange={(ev) => onChange({ symbol: ev.target.value })} className={SEL} data-testid={`legend-symbol-${kind}`}>{SYMBOLS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select></td>
      <td className="py-1.5 pr-2"><select disabled={!canEdit || mt === "count"} value={e.line_style || "solid"} onChange={(ev) => onChange({ line_style: ev.target.value })} className={SEL} data-testid={`legend-linestyle-${kind}`}>{LINE_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}</select></td>
      <td className="py-1.5 pr-2"><select disabled={!canEdit || mt === "count"} value={String(e.width || 2)} onChange={(ev) => onChange({ width: Number(ev.target.value) })} className={SEL} data-testid={`legend-width-${kind}`}>{WIDTHS.map((w) => <option key={w} value={String(w)}>{w}</option>)}</select></td>
      <td className="py-1.5"><select disabled={!canEdit || (mt !== "area" && e.measure_type != null)} value={e.hatch || "none"} onChange={(ev) => onChange({ hatch: ev.target.value })} className={SEL} data-testid={`legend-hatch-${kind}`}>{HATCHES.map((h) => <option key={h} value={h}>{h}</option>)}</select></td>
    </tr>
  );
}

// Company setting: one colour and one shape per thing, for every bid's takeoff.
export function TakeoffLegendSetting({ canEdit = true }) {
  const [legend, setLegend] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/settings/takeoff-legend").then((r) => setLegend(r.data.legend)).catch(() => setLegend(null)); }, []);
  const save = async (body, msg) => {
    try { const { data } = await api.put("/settings/takeoff-legend", body); setLegend(data.legend); setLegendCache(data.legend); toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save the legend"); }
  };
  if (!legend) return null;
  return (
    <Card className="p-4 mb-6" data-testid="takeoff-legend-setting">
      <div className="flex items-start gap-3">
        <Palette className="w-5 h-5 text-[#003E87] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <button onClick={() => setOpen((o) => !o)} className="text-sm font-semibold text-slate-900 flex items-center gap-1" data-testid="takeoff-legend-toggle">
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />} Takeoff legend
          </button>
          <div className="text-xs text-slate-500">Every bid uses the same colour and shape for the same thing. New layers take it when you pick their kind. <Link to="/how-to/takeoff_legend" className="text-[#003E87] hover:underline">How it works</Link></div>
        </div>
        {open && canEdit && <Button variant="outline" size="sm" onClick={() => save({ reset: true }, "Legend reset to the standard")} data-testid="takeoff-legend-reset">Reset to standard</Button>}
      </div>
      {open && (
        <div className="overflow-x-auto mt-3">
          <table className="w-full min-w-[560px]">
            <thead><tr className="text-[10px] uppercase tracking-wide text-slate-400 text-left"><th></th><th>Kind</th><th>Colour</th><th>Symbol</th><th>Line</th><th>Width</th><th>Hatch</th></tr></thead>
            <tbody>{Object.entries(legend).map(([k, e]) => <LegendRow key={k} kind={k} e={e} canEdit={canEdit} onChange={(ch) => save({ entries: { [k]: ch } }, `${kindLabel(k)} saved`)} />)}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
